import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Form as AntdForm, Popconfirm } from 'antd';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';
import {
  useGetPhdDissertationExamApplicationQuery,
  useSavePhdDissertationExamApplicationMutation,
  useLazyGetPhdDissertationExamApplicationDocumentQuery,
  useSubmitPhdDissertationExamApplicationMutation,
} from '../../../../../redux/services/phd';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import PhdDissertationExamApplicationsEditForm from './PhdDissertationExamApplicationsEditForm.jsx';
import usePhdStudentProfile, {
  mapStudentProfileToFormDefaults,
} from '../usePhdStudentProfile.jsx';
import { downloadBlobResponse } from '../../../../../utils/helpers/downloadBlobResponse';
import {
  isApplicationSubmitted,
  PHD_APPLICATION_CHANGE_CONTACT_MESSAGE,
} from '../utils/applicationSubmissionState.jsx';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';
import PhdArchivedRecordNotice from '../PhdArchivedRecordNotice.jsx';

const PhdDissertationExamApplicationsEdit = ({ mode }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const isAdmin = useIsUserPhdAdmin();
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const pageTitle = isCreate
    ? t('Pridanie prihlášky na dizertačnú skúšku')
    : t('Editácia prihlášky na dizertačnú skúšku');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const { data: detail, isLoading } = useGetPhdDissertationExamApplicationQuery(
    id,
    {
      skip: !isEdit,
    },
  );
  const [saveApplication, { isLoading: isSaving }] =
    useSavePhdDissertationExamApplicationMutation();
  const [downloadApplicationDoc, { isLoading: isDownloading }] =
    useLazyGetPhdDissertationExamApplicationDocumentQuery();
  const [submitApplication, { isLoading: isSubmittingApplication }] =
    useSubmitPhdDissertationExamApplicationMutation();
  const { saveSuccess, saveFailed, successMessage } = useToastMessages();
  const { data: studentProfile, isFetching: isStudentLoading } =
    usePhdStudentProfile();
  const isStudentContext = Boolean(studentProfile?.id) && !isAdmin;
  const isReadOnly =
    detail?.is_read_only === true ||
    (isEdit && isApplicationSubmitted(detail) && !isAdmin);

  const {
    studentOptions,
    students,
    schoolYearOptions,
    studyFieldLabelOptions,
    ensureSchoolYearSelection,
    isCreatingSchoolYear,
    isLoading: isReferenceLoading,
  } = usePhdReferenceData({ includeStudents: !isStudentContext });

  const initials = useMemo(() => {
    const applyStudentContextDefaults = (values) => {
      if (!isStudentContext) {
        return values;
      }
      const studentDefaults = mapStudentProfileToFormDefaults(studentProfile);
      return {
        ...values,
        ...studentDefaults,
      };
    };

    if (isCreate) {
      const base = {
        phd_student_id: undefined,
        phd_school_year_id: undefined,
        thesis_title_sk: undefined,
        thesis_title_en: undefined,
        student_display_name: undefined,
        supervisor_display_name: undefined,
        student_study_form: undefined,
        student_study_field: undefined,
        student_study_program: undefined,
      };

      return applyStudentContextDefaults(base);
    }
    if (!detail) {
      return null;
    }
    return applyStudentContextDefaults({ ...detail });
  }, [detail, isCreate, isStudentContext, studentProfile]);
  const { modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, initials);

  const handleSubmit = async (values) => {
    try {
      await saveApplication({
        id: isEdit ? detail.id : undefined,
        body: values,
      }).unwrap();
      saveSuccess();
      onSaveSuccess(() =>
        navigate(
          isStudentContext
            ? routes.cmsPhdApplications
            : routes.cmsPhdDissertationExamApplications,
        ),
      );
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  const handleDownload = async () => {
    if (!detail?.id) return;
    try {
      const response = await downloadApplicationDoc(detail.id).unwrap();
      downloadBlobResponse(
        response,
        `${detail.student_display_name || t('Prihláška na dizertačnú skúšku')}.docx`,
      );
    } catch (error) {
      saveFailed(error);
    }
  };

  const mainActions = useMemo(() => {
    if (!isEdit || !detail) return [];
    if (!isReadOnly) {
      return [];
    }

    return [
      {
        onClick: handleDownload,
        title: detail.is_frozen
          ? t('Stiahnuť DOCX')
          : t('Dokument ešte nemá zmrazenú archívnu verziu'),
        icon: <i className="fa fa-download" />,
        permission: 'phd-dissertation-exam-applications.index',
        disabled: isDownloading || !detail.is_frozen,
      },
    ];
  }, [detail, handleDownload, isDownloading, isEdit, isReadOnly, t]);

  const handleSubmitApplication = async () => {
    if (!detail?.id) return;

    try {
      await submitApplication(detail.id).unwrap();
      successMessage({ message: t('Prihláška bola odovzdaná') });
      navigate(routes.cmsPhdApplications);
    } catch (error) {
      saveFailed(error);
    }
  };

  if (
    (isEdit && isLoading) ||
    isReferenceLoading ||
    !initials ||
    isStudentLoading
  ) {
    return <Spinner />;
  }

  return (
    <>
      {!isReadOnly && modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={
          isStudentContext
            ? routes.cmsPhdApplications
            : routes.cmsPhdDissertationExamApplications
        }
        backUrlTooltip={t('Prihlášky na dizertačnú skúšku')}
        permissionsBasic="phd-dissertation-exam-applications"
        mainActions={mainActions}
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje prihláška na dizertačnú skúšku?')}
          description={t(
            'Doktorand alebo administrátor doktorandského štúdia pripraví prihlášku z údajov individuálneho plánu. Po kontrole predmetov ju odovzdá; následne možno pripraviť návrh skúšobnej komisie.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-file-text"
          link={routes.cmsPhdDissertationExamApplications}
          title={pageTitle}
        >
          <PhdArchivedRecordNotice record={detail} />
          {isStudentContext ? (
            <Alert
              type={isReadOnly ? 'info' : 'warning'}
              showIcon
              style={{ marginBottom: 16 }}
              message={
                isReadOnly
                  ? t(PHD_APPLICATION_CHANGE_CONTACT_MESSAGE)
                  : t(
                      'Prihláška je zatiaľ rozpracovaná. Po potvrdení a odovzdaní ju už nebude možné meniť ani vymazať.',
                    )
              }
            />
          ) : null}
          {isStudentContext && isEdit && !isReadOnly ? (
            <Popconfirm
              title={t('Naozaj chcete potvrdiť a odovzdať prihlášku?')}
              description={t(
                'Po odovzdaní už nebude možné prihlášku upravovať ani vymazať.',
              )}
              okText={t('Odovzdať')}
              cancelText={t('Zrušiť')}
              onConfirm={handleSubmitApplication}
            >
              <button
                type="button"
                className="ant-btn ant-btn-default"
                style={{ marginBottom: 16 }}
                disabled={isSubmittingApplication}
              >
                {t('Potvrdiť a odovzdať')}
              </button>
            </Popconfirm>
          ) : null}
          <Form
            form={form}
            initialValues={initials}
            onFinish={handleSubmit}
            onValuesChange={onValuesChange}
            disabled={isReadOnly}
          >
            <PhdDissertationExamApplicationsEditForm
              studentOptions={studentOptions}
              students={isStudentContext ? [studentProfile] : students}
              schoolYearOptions={schoolYearOptions}
              ensureSchoolYearSelection={ensureSchoolYearSelection}
              isCreatingSchoolYear={isCreatingSchoolYear}
              studyFieldLabelOptions={studyFieldLabelOptions}
              isSubmitting={isSaving}
              isStudentContext={isStudentContext}
              isReadOnly={isReadOnly}
            />
          </Form>
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdDissertationExamApplicationsEdit;
